import { Processing } from '@paywithextend/components'
import axios from 'axios'
import React from 'react'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { PostExpenseReportsDownloadRequestParams } from 'src/app/DownloadExpenseReports/DownloadExpenseReports'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  DownloadFromEmailToastMessage,
  DownloadToastMessage,
} from 'src/app/Toast/Toast'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { TransactionsCSVFields } from 'src/cookies'
import { DownloadStatus, PeopleSubSections } from 'src/enums'
import { ReportDownloadListResponse } from 'src/models/reportDownload.models'
import { closeModal } from 'src/navigation/Lnk'
import {
  createSetIsDownloadExpenseReportsPaidPopupOpen,
  createSetIsDownloadSuccessPopupOpen,
} from 'src/redux/actions/app.actions'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { PostOrgExpenseReportDownloadRequestParams } from 'src/requests/postOrgExpenseReportDownload.requests'
import { BackendErrorPayload } from 'src/utils/error.utils'

export const POST_REPORT_TRANSACTIONS_DOWNLOAD_INIT =
  'data/reportDownloads/POST_REPORT_TRANSACTIONS_DOWNLOAD_INIT'
export const POST_REPORT_TRANSACTIONS_DOWNLOAD_SUCCESS =
  'data/reportDownloads/POST_REPORT_TRANSACTIONS_DOWNLOAD_SUCCESS'
export const POST_REPORT_TRANSACTIONS_DOWNLOAD_FAILURE =
  'data/reportDownloads/POST_REPORT_TRANSACTIONS_DOWNLOAD_FAILURE'

export interface PostReportTransactionsDownloadSuccessPayload {
  reportDownload: ReportDownloadListResponse
}

export type PostReportTransactionsDownloadFailurePayload =
  BackendErrorPayload<string>

export interface PostReportTransactionsDownloadInitAction {
  type: typeof POST_REPORT_TRANSACTIONS_DOWNLOAD_INIT
}
export interface PostReportTransactionsDownloadSuccessAction {
  type: typeof POST_REPORT_TRANSACTIONS_DOWNLOAD_SUCCESS
  payload: PostReportTransactionsDownloadSuccessPayload
}
export interface PostReportTransactionsDownloadFailureAction {
  type: typeof POST_REPORT_TRANSACTIONS_DOWNLOAD_FAILURE
  payload: PostReportTransactionsDownloadFailurePayload
}

export type PostReportTransactionsDownloadActions =
  | PostReportTransactionsDownloadInitAction
  | PostReportTransactionsDownloadSuccessAction
  | PostReportTransactionsDownloadFailureAction

const createPostReportTransactionsDownloadInitAction =
  (): PostReportTransactionsDownloadInitAction => ({
    type: POST_REPORT_TRANSACTIONS_DOWNLOAD_INIT,
  })
const createPostReportTransactionsDownloadSuccessAction = (
  payload: PostReportTransactionsDownloadSuccessPayload,
): PostReportTransactionsDownloadSuccessAction => ({
  type: POST_REPORT_TRANSACTIONS_DOWNLOAD_SUCCESS,
  payload,
})
const createPostReportTransactionsDownloadFailureAction = (
  err: PostReportTransactionsDownloadFailurePayload,
): PostReportTransactionsDownloadFailureAction => ({
  type: POST_REPORT_TRANSACTIONS_DOWNLOAD_FAILURE,
  payload: err,
})

export const postReportTransactionsDownload = (
  body: TransactionsFiltersBody,
  qboReport: boolean,
  attachments: boolean,
  transactionCSVFields: TransactionsCSVFields,
  onClose?: () => void,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostReportTransactionsDownloadActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostReportTransactionsDownloadActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostReportTransactionsDownloadInitAction())
    return axios
      .post(
        `/reports/transactions/download`,
        {
          ...body,
          qboReport,
          attachments,
          transactionCSVFields,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createPostReportTransactionsDownloadSuccessAction(response.data),
          )
          onClose?.()
          dispatch(createSetIsDownloadSuccessPopupOpen(true))
        },
        (err) => {
          dispatch(
            createPostReportTransactionsDownloadFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_INIT =
  'data/reportDownloads/POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_INIT'
export const POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_SUCCESS =
  'data/reportDownloads/POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_SUCCESS'
export const POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_FAILURE =
  'data/reportDownloads/POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_FAILURE'

export interface PostReportVirtualCardsDownloadSuccessPayload {
  reportDownload: ReportDownloadListResponse
}

export type PostReportVirtualCardsDownloadFailurePayload =
  BackendErrorPayload<string>

export interface PostReportVirtualCardsDownloadInitAction {
  type: typeof POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_INIT
}
export interface PostReportVirtualCardsDownloadSuccessAction {
  type: typeof POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_SUCCESS
  payload: PostReportVirtualCardsDownloadSuccessPayload
}
export interface PostReportVirtualCardsDownloadFailureAction {
  type: typeof POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_FAILURE
  payload: PostReportVirtualCardsDownloadFailurePayload
}

export type PostReportVirtualCardsDownloadActions =
  | PostReportVirtualCardsDownloadInitAction
  | PostReportVirtualCardsDownloadSuccessAction
  | PostReportVirtualCardsDownloadFailureAction

const createPostReportVirtualCardsDownloadInitAction =
  (): PostReportVirtualCardsDownloadInitAction => ({
    type: POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_INIT,
  })
const createPostReportVirtualCardsDownloadSuccessAction = (
  payload: PostReportVirtualCardsDownloadSuccessPayload,
): PostReportVirtualCardsDownloadSuccessAction => ({
  type: POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_SUCCESS,
  payload,
})
const createPostReportVirtualCardsDownloadFailureAction = (
  err: PostReportVirtualCardsDownloadFailurePayload,
): PostReportVirtualCardsDownloadFailureAction => ({
  type: POST_REPORT_VIRTUAL_CARDS_DOWNLOAD_FAILURE,
  payload: err,
})

export const postReportVirtualCardsDownload = (
  filters: VirtualCardsFiltersBody,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostReportVirtualCardsDownloadActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostReportVirtualCardsDownloadActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostReportVirtualCardsDownloadInitAction())
    return axios
      .post<PostReportVirtualCardsDownloadSuccessPayload>(
        `/reports/virtualcards/download`,
        filters,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createPostReportVirtualCardsDownloadSuccessAction(response.data),
          )
          dispatch(createSetIsDownloadSuccessPopupOpen(true))
        },
        (err) => {
          dispatch(
            createPostReportVirtualCardsDownloadFailureAction(
              err.response.data,
            ),
          )
        },
      )
  }
}

export const POST_ORG_EXPENSE_REPORT_DOWNLOAD_INIT =
  'data/reportDownloads/POST_ORG_EXPENSE_REPORT_DOWNLOAD_INIT'
export const POST_ORG_EXPENSE_REPORT_DOWNLOAD_SUCCESS =
  'data/reportDownloads/POST_ORG_EXPENSE_REPORT_DOWNLOAD_SUCCESS'
export const POST_ORG_EXPENSE_REPORT_DOWNLOAD_FAILURE =
  'data/reportDownloads/POST_ORG_EXPENSE_REPORT_DOWNLOAD_FAILURE'

export interface PostOrgExpenseReportDownloadSuccessPayload {
  reportDownload: ReportDownloadListResponse
}

export type PostOrgExpenseReportDownloadFailurePayload =
  BackendErrorPayload<string>

export interface PostOrgExpenseReportDownloadInitAction {
  type: typeof POST_ORG_EXPENSE_REPORT_DOWNLOAD_INIT
}
export interface PostOrgExpenseReportDownloadSuccessAction {
  type: typeof POST_ORG_EXPENSE_REPORT_DOWNLOAD_SUCCESS
  payload: PostOrgExpenseReportDownloadSuccessPayload
}
export interface PostOrgExpenseReportDownloadFailureAction {
  type: typeof POST_ORG_EXPENSE_REPORT_DOWNLOAD_FAILURE
  payload: PostOrgExpenseReportDownloadFailurePayload
}

export type PostOrgExpenseReportDownloadActions =
  | PostOrgExpenseReportDownloadInitAction
  | PostOrgExpenseReportDownloadSuccessAction
  | PostOrgExpenseReportDownloadFailureAction

const createPostOrgExpenseReportDownloadInitAction =
  (): PostOrgExpenseReportDownloadInitAction => ({
    type: POST_ORG_EXPENSE_REPORT_DOWNLOAD_INIT,
  })
const createPostOrgExpenseReportDownloadSuccessAction = (
  payload: PostOrgExpenseReportDownloadSuccessPayload,
): PostOrgExpenseReportDownloadSuccessAction => ({
  type: POST_ORG_EXPENSE_REPORT_DOWNLOAD_SUCCESS,
  payload,
})
const createPostOrgExpenseReportDownloadFailureAction = (
  err: PostOrgExpenseReportDownloadFailurePayload,
): PostOrgExpenseReportDownloadFailureAction => ({
  type: POST_ORG_EXPENSE_REPORT_DOWNLOAD_FAILURE,
  payload: err,
})

export const postOrgExpenseReportDownload = (
  body: PostOrgExpenseReportDownloadRequestParams,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostOrgExpenseReportDownloadActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PostOrgExpenseReportDownloadActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostOrgExpenseReportDownloadInitAction())
    return axios
      .post(
        `/reports/expensereports/download`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(
            createPostOrgExpenseReportDownloadSuccessAction(response.data),
          )
          dispatch(closeModal())
          dispatch(createSetIsDownloadSuccessPopupOpen(true))
        },
        (err) => {
          dispatch(
            createPostOrgExpenseReportDownloadFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_REPORT_DOWNLOAD_INIT =
  'data/reportDownloads/GET_REPORT_DOWNLOAD_INIT'
export const GET_REPORT_DOWNLOAD_SUCCESS =
  'data/reportDownloads/GET_REPORT_DOWNLOAD_SUCCESS'
export const GET_REPORT_DOWNLOAD_FAILURE =
  'data/reportDownloads/GET_REPORT_DOWNLOAD_FAILURE'

export type GetReportDownloadSuccessPayload = {
  reportDownload: ReportDownloadListResponse
}

export interface GetReportDownloadInitAction {
  type: typeof GET_REPORT_DOWNLOAD_INIT
}
export interface GetReportDownloadSuccessAction {
  type: typeof GET_REPORT_DOWNLOAD_SUCCESS
  payload: GetReportDownloadSuccessPayload
}
export interface GetReportDownloadFailureAction {
  type: typeof GET_REPORT_DOWNLOAD_FAILURE
  payload: BackendErrorPayload<string>
}

export type GetReportDownloadActions =
  | GetReportDownloadInitAction
  | GetReportDownloadSuccessAction
  | GetReportDownloadFailureAction

const createGetReportDownloadInitAction = (): GetReportDownloadInitAction => ({
  type: GET_REPORT_DOWNLOAD_INIT,
})
const createGetReportDownloadSuccessAction = (
  payload: GetReportDownloadSuccessPayload,
): GetReportDownloadSuccessAction => ({
  type: GET_REPORT_DOWNLOAD_SUCCESS,
  payload,
})
const createGetReportDownloadFailureAction = (
  response: BackendErrorPayload<string>,
): GetReportDownloadFailureAction => ({
  type: GET_REPORT_DOWNLOAD_FAILURE,
  payload: response,
})

export const getReportDownload = (
  reportDownloadId: string,
): ThunkAction<
  Promise<GetReportDownloadSuccessAction | GetReportDownloadFailureAction>,
  RootState,
  void,
  GetReportDownloadActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetReportDownloadActions>,
    getState: () => RootState,
  ): Promise<
    GetReportDownloadSuccessAction | GetReportDownloadFailureAction
  > => {
    dispatch(createGetReportDownloadInitAction())
    return axios
      .get<GetReportDownloadSuccessPayload>(
        `/reports/${reportDownloadId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <DownloadFromEmailToastMessage
                  status={DownloadStatus.Success}
                />
              ),
            }),
          )
          if (response.data.reportDownload.fileUrl !== undefined) {
            window.open(response.data.reportDownload.fileUrl)
          }
          return dispatch(createGetReportDownloadSuccessAction(response.data))
        },
        async (err) => {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <DownloadFromEmailToastMessage status={DownloadStatus.Error} />
              ),
              failureColorScheme: true,
            }),
          )
          return dispatch(
            createGetReportDownloadFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_PEOPLE_DOWNLOAD_INIT =
  'data/reportDownloads/POST_PEOPLE_DOWNLOAD_INIT'
export const POST_PEOPLE_DOWNLOAD_SUCCESS =
  'data/reportDownloads/POST_PEOPLE_DOWNLOAD_SUCCESS'
export const POST_PEOPLE_DOWNLOAD_FAILURE =
  'data/reportDownloads/POST_PEOPLE_DOWNLOAD_FAILURE'

export interface PostPeopleDownloadSuccessPayload {
  reportDownload: ReportDownloadListResponse
}

export type PostPeopleDownloadFailurePayload = BackendErrorPayload<string>

export interface PostPeopleDownloadInitAction {
  type: typeof POST_PEOPLE_DOWNLOAD_INIT
}
export interface PostPeopleDownloadSuccessAction {
  type: typeof POST_PEOPLE_DOWNLOAD_SUCCESS
  payload: PostPeopleDownloadSuccessPayload
}
export interface PostPeopleDownloadFailureAction {
  type: typeof POST_PEOPLE_DOWNLOAD_FAILURE
  payload: PostPeopleDownloadFailurePayload
}

export type PostPeopleDownloadActions =
  | PostPeopleDownloadInitAction
  | PostPeopleDownloadSuccessAction
  | PostPeopleDownloadFailureAction

const createPostPeopleDownloadInitAction =
  (): PostPeopleDownloadInitAction => ({
    type: POST_PEOPLE_DOWNLOAD_INIT,
  })
const createPostPeopleDownloadSuccessAction = (
  payload: PostPeopleDownloadSuccessPayload,
): PostPeopleDownloadSuccessAction => ({
  type: POST_PEOPLE_DOWNLOAD_SUCCESS,
  payload,
})
const createPostPeopleDownloadFailureAction = (
  err: PostPeopleDownloadFailurePayload,
): PostPeopleDownloadFailureAction => ({
  type: POST_PEOPLE_DOWNLOAD_FAILURE,
  payload: err,
})

export const postPeopleDownload = (
  type: PeopleSubSections,
): ThunkAction<Promise<void>, RootState, void, PostPeopleDownloadActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostPeopleDownloadActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostPeopleDownloadInitAction())
    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <DownloadToastMessage />,
        inProgressColorScheme: true,
        image: <Processing isLod={true} />,
        isInfinite: true,
      }),
    )
    return axios
      .post<PostPeopleDownloadSuccessPayload>(
        `/reports/${type}/download`,
        {},
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createSetIsToastOpen(false))
          dispatch(createPostPeopleDownloadSuccessAction(response.data))
          if (response.data.reportDownload.fileUrl !== undefined) {
            window.open(response.data.reportDownload.fileUrl)
          }
        },
        (err) => {
          dispatch(createSetIsToastOpen(false))
          dispatch(createPostPeopleDownloadFailureAction(err.response.data))
        },
      )
  }
}
export const CLEAR_TOP_LEVEL_ERROR_EXPENSE_REPORTS =
  'data/reportDownloads/CLEAR_TOP_LEVEL_ERROR_EXPENSE_REPORTS'

export const POST_EXPENSE_REPORT_DOWNLOAD_INIT =
  'data/reportDownloads/POST_EXPENSE_REPORT_DOWNLOAD_INIT'
export const POST_EXPENSE_REPORT_DOWNLOAD_SUCCESS =
  'data/reportDownloads/POST_EXPENSE_REPORT_DOWNLOAD_SUCCESS'
export const POST_EXPENSE_REPORT_DOWNLOAD_FAILURE =
  'data/reportDownloads/POST_EXPENSE_REPORT_DOWNLOAD_FAILURE'

export interface PostExpenseReportDownloadSuccessPayload {
  reportDownload: ReportDownloadListResponse
}

export type PostExpenseReportDownloadFailurePayload =
  BackendErrorPayload<string>

export interface PostExpenseReportDownloadInitAction {
  type: typeof POST_EXPENSE_REPORT_DOWNLOAD_INIT
}
export interface PostExpenseReportDownloadSuccessAction {
  type: typeof POST_EXPENSE_REPORT_DOWNLOAD_SUCCESS
  payload: PostExpenseReportDownloadSuccessPayload
}
export interface PostExpenseReportDownloadFailureAction {
  type: typeof POST_EXPENSE_REPORT_DOWNLOAD_FAILURE
  payload: PostExpenseReportDownloadFailurePayload
}
export interface ClearTopLevelErrorExpenseReportsAction {
  type: typeof CLEAR_TOP_LEVEL_ERROR_EXPENSE_REPORTS
}

export type PostExpenseReportDownloadActions =
  | PostExpenseReportDownloadInitAction
  | PostExpenseReportDownloadSuccessAction
  | PostExpenseReportDownloadFailureAction
  | ClearTopLevelErrorExpenseReportsAction

const createPostExpenseReportDownloadInitAction =
  (): PostExpenseReportDownloadInitAction => ({
    type: POST_EXPENSE_REPORT_DOWNLOAD_INIT,
  })
const createPostExpenseReportDownloadSuccessAction = (
  payload: PostExpenseReportDownloadSuccessPayload,
): PostExpenseReportDownloadSuccessAction => ({
  type: POST_EXPENSE_REPORT_DOWNLOAD_SUCCESS,
  payload,
})
const createPostExpenseReportDownloadFailureAction = (
  err: PostExpenseReportDownloadFailurePayload,
): PostExpenseReportDownloadFailureAction => ({
  type: POST_EXPENSE_REPORT_DOWNLOAD_FAILURE,
  payload: err,
})

// OOPE
export const postExpenseReportDownload = (
  queryParameters?: PostExpenseReportsDownloadRequestParams,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostExpenseReportDownloadActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostExpenseReportDownloadActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostExpenseReportDownloadInitAction())
    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: <DownloadToastMessage />,
        inProgressColorScheme: true,
        image: <Processing isLod={true} />,
        isInfinite: true,
        isDarkBackground: true,
      }),
    )
    return axios
      .post(
        `/expensereports/approver/download`,
        queryParameters,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createSetIsToastOpen(false))
          dispatch(createSetIsDownloadExpenseReportsPaidPopupOpen(false))
          dispatch(createPostExpenseReportDownloadSuccessAction(response.data))
          if (response.data.reportDownload.fileUrl !== undefined) {
            window.open(response.data.reportDownload.fileUrl)
          }
        },
        (err) => {
          dispatch(createSetIsToastOpen(false))
          dispatch(
            createPostExpenseReportDownloadFailureAction(err.response.data),
          )
        },
      )
  }
}
